import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Circle as ColorIcon } from '@mui/icons-material';
import type {
  NoteWithRelations,
  CreateNoteRequest,
  UpdateNoteRequest,
  NoteType,
} from '../../types/note';
import { NOTE_TYPE_OPTIONS, NOTE_COLORS } from '../../types/note';

interface NoteEditorProps {
  note?: NoteWithRelations | null;
  onChange: (data: CreateNoteRequest | UpdateNoteRequest) => void;
  clients?: Array<{ _id: string; firstName: string; lastName: string }>;
  autoFocus?: boolean;
  compact?: boolean;
}

export const NoteEditor: React.FC<NoteEditorProps> = ({
  note,
  onChange,
  clients = [],
  autoFocus = false,
  compact = false,
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<NoteType>('quick');
  const [color, setColor] = useState<string | undefined>(undefined);
  const [clientId, setClientId] = useState<string>('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // Initialize form when note changes
  useEffect(() => {
    if (note) {
      setTitle(note.title);
      setContent(note.content);
      setType(note.type);
      setColor(note.color);
      setClientId(note.clientId?._id || '');
      setTags(note.tags || []);
    } else {
      setTitle('');
      setContent('');
      setType('quick');
      setColor(undefined);
      setClientId('');
      setTags([]);
    }
  }, [note]);

  // Emit changes
  useEffect(() => {
    onChange({
      title: title.trim(),
      content,
      type,
      color,
      clientId: clientId || undefined,
      tags,
    });
  }, [title, content, type, color, clientId, tags]);

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <Stack spacing={compact ? 2 : 3}>
      {/* Title */}
      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        autoFocus={autoFocus}
        required
        placeholder="What's on your mind?"
        variant={compact ? 'standard' : 'outlined'}
      />

      {/* Content */}
      <TextField
        label="Content"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        multiline
        rows={compact ? 3 : 6}
        fullWidth
        placeholder="Add details, notes, or context..."
        variant={compact ? 'standard' : 'outlined'}
      />

      {/* Type Selection */}
      <Box>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          Note Type
        </Typography>
        <ToggleButtonGroup
          value={type}
          exclusive
          onChange={(_, newType) => newType && setType(newType)}
          size="small"
          sx={{ flexWrap: 'wrap' }}
        >
          {NOTE_TYPE_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.icon} {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Color Selection */}
      {!compact && (
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            Color (optional)
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => setColor(undefined)}
              sx={{
                border: !color ? '2px solid' : 'none',
                borderColor: 'primary.main',
              }}
            >
              <ColorIcon sx={{ color: 'grey.300' }} />
            </IconButton>
            {NOTE_COLORS.map((c) => (
              <IconButton
                key={c}
                size="small"
                onClick={() => setColor(c)}
                sx={{
                  border: color === c ? '2px solid' : 'none',
                  borderColor: 'primary.main',
                }}
              >
                <ColorIcon sx={{ color: c }} />
              </IconButton>
            ))}
          </Stack>
        </Box>
      )}

      {/* Client Selection */}
      {clients.length > 0 && (
        <FormControl fullWidth size={compact ? 'small' : 'medium'}>
          <InputLabel>Link to Client (optional)</InputLabel>
          <Select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            label="Link to Client (optional)"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {clients.map((client) => (
              <MenuItem key={client._id} value={client._id}>
                {client.firstName} {client.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Tags */}
      {!compact && (
        <Box>
          <TextField
            label="Add Tags"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            onBlur={handleAddTag}
            fullWidth
            size="small"
            placeholder="Press Enter to add"
          />
          {tags.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  size="small"
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Stack>
  );
};

export default NoteEditor;
